// LinkJobsDlg.js
import React from "react";
import { AccentDialog } from "./AccentDialog";
import { AccentButton } from "../controls/AccentButton";
import ChatJobSelector from "../controls/chat/ChatJobSelector";

export default function LinkJobsDlg(props) {
    const {
        chatIntId,                 // e.g. 22
        chatSubject,               // "Blinds quote"

        // tolerate both spellings
        contactId: contactIdProp = null,
        contactID: contactIDProp = null,

        initialSelectedJobIds,     // from helper
        initiallyLinkedIds,        // legacy/new name
        initialSelectedJobs,       // from helper
        initiallyLinkedJobs,       // legacy/new name

        onDone,                    // expects: ({ jobIds }) => ...
        onClose,
    } = props;

    // normalise props (fixes #1 and #3)
    const contactId = contactIdProp ?? contactIDProp ?? null; // enable Suggested when set  :contentReference[oaicite:8]{index=8}
    const linkedIds = initialSelectedJobIds ?? initiallyLinkedIds ?? [];         // show + preselect  :contentReference[oaicite:9]{index=9}
    const linkedJobs = initialSelectedJobs ?? initiallyLinkedJobs ?? [];         // render labels     :contentReference[oaicite:10]{index=10}

    const [selectedIds, setSelectedIds] = React.useState(linkedIds.slice());
    const [busy, setBusy] = React.useState(false);
    const selectorRef = React.useRef(null);

    const header = (
        <div className="fw-semibold">
            {`Link Job(s) to Chat - #${chatIntId ?? "—"}${chatSubject ? `: ${chatSubject}` : ""}`}
        </div>
    );

    const handleSave = async () => {
        if (busy) return;
        try {
            setBusy(true);
            // read the ACTUAL selection from the grid (checkbox + row clicks)
            const rows = selectorRef.current?.getSelectedRows?.() || [];
            const jobIds = rows.map(r => r.ID);
            const res = await onDone?.({ jobIds });            // preserves existing helper API
            // also return the rows so the caller can refresh the “LinkedJobs” list immediately
            onClose?.({ linkedJobs: rows.map(r => ({ JobID: r.ID, Reference: r.Reference })) });
            } finally {
            setBusy(false);
        }
    };

    const onChangeSelected = React.useCallback((rows) => {
        setSelectedIds(Array.isArray(rows) ? rows.map(r => r.ID) : []);
    }, []);

    const handleClear = (e) => {
        e?.preventDefault?.();
        // Clear both the grid selection and our local state
        selectorRef.current?.select?.([]);
        setSelectedIds([]);
    };

    const handleSelectAll = (e) => {
        e?.preventDefault?.();
        // Select the chat’s currently-linked jobs (not every job in the grid)
        selectorRef.current?.select?.(linkedIds);
        setSelectedIds(linkedIds.slice());
    };

    // Auto-select the chat's currently linked jobs on first render
    const didInit = React.useRef(false);
    React.useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;
        if (linkedIds?.length) {
            selectorRef.current?.select?.(linkedIds);
            setSelectedIds(linkedIds.slice());
        }
    }, [linkedIds]);

    return (
        <AccentDialog
            id="dlg-link-jobs"
            headerContent={header}
            minWidth={1100}
            minHeight={560}
            maxWidth={1440}
            onClose={onClose}
            actionsContent={[
                <AccentButton key="link" disabled={busy} onClick={handleSave}>
                    Link
                </AccentButton>,
            ]}
        >
            <div>

                {/* strip restored to original placement — fixes #4 */}
                <div className="small text-muted mb-2">
                    <span>Currently linked: </span>
                    {linkedJobs.length > 0 ? (
                        <>
                            {linkedJobs.map((j, i) => (
                                <span key={j.id}>
                                    {i > 0 ? " | " : ""}
                                    <a href={`#/jobs/${j.id}`} onClick={(ev) => ev.preventDefault()}>
                                        {j.ref || `Job #${j.id}`}
                                    </a>
                                </span>
                            ))}
                            <span className="ms-3">
                                <a href="#" className="me-2" onClick={handleSelectAll}>Select all</a>
                                <a href="#" onClick={handleClear}>Clear</a>
                            </span>
                        </>
                    ) : (
                        <em>none</em>
                    )}
                </div>

                <ChatJobSelector
                    contactID={contactId}                 // enables Suggested when present — fixes #3  :contentReference[oaicite:12]{index=12}
                    multiple
                    selectedIds={selectedIds}
                    onChangeSelected={onChangeSelected}
                    height={480}
                    methodsRef={selectorRef}              // used by Select all
                    gridId="link_jobs"
                />

                <div className="text-muted small mt-2">
                    Tip: select one or more jobs with the checkboxes.
                </div>
            </div>
        </AccentDialog>
    );
}
